import { ThemeButton } from './Theme';
import React, { useEffect, useState, useRef } from 'react';
import { Settings, Play, Cloud, Square, Loader2, AlertTriangle, CheckCircle2, Circle } from 'lucide-react';
import { tradingApi } from '../services/api';
import GatewayConfigModal from './GatewayConfigModal';

const InvestorQuickStart: React.FC = () => {
    const [showConfigModal, setShowConfigModal] = useState(false);
    const [controlState, setControlState] = useState<any>(null);
    const [loading, setLoading] = useState(false);
    const [activeAction, setActiveAction] = useState<"start" | "stop" | null>(null);
    const [gatewayError, setGatewayError] = useState<string | null>(null);
    const [setupRefreshing, setSetupRefreshing] = useState(false);
    const abortControllerRef = useRef<AbortController | null>(null);

    const fetchControlState = async () => {
        try {
            const response = await tradingApi.getSystemControlState();
            if (response.success && response.data) {
                setControlState(response.data);
            }
        } catch (err) {
            console.warn("Failed to fetch control state:", err);
        }
    };

    useEffect(() => {
        fetchControlState();
        const id = setInterval(fetchControlState, 5000);
        return () => clearInterval(id);
    }, []);

    useEffect(() => {
        if (!gatewayError) return;
        const timer = setTimeout(() => setGatewayError(null), 10000);
        return () => clearTimeout(timer);
    }, [gatewayError]);

    useEffect(() => {
        return () => {
            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
            }
        };
    }, []);

    const handleConfigureClick = async () => {
        setLoading(true);
        setGatewayError(null);
        try {
            const response = await tradingApi.getSystemControlState();
            if (response.success && response.data) {
                setControlState(response.data);
                setShowConfigModal(true);
            }
        } catch (err) {
            console.warn("Failed to open configuration:", err);
            setShowConfigModal(true);
        } finally {
            setLoading(false);
        }
    };

    const isGatewayActive = controlState?.gateway_running ?? controlState?.ib_connected ?? false;
    const isBrokerConnected = controlState?.ib_connected ?? false;
    const isConfigured = controlState?.gateway_configured ?? false;
    const showSetupPreparing = setupRefreshing && !isConfigured;

    const handleGatewayConfigSuccess = async () => {
        setSetupRefreshing(true);
        setGatewayError(null);
        await fetchControlState();
        setTimeout(() => setSetupRefreshing(false), 2500);
    };

    const handleCancel = async () => {
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
            abortControllerRef.current = null;
        }
        setLoading(false);
        setActiveAction(null);

        try {
            // Explicitly stop the gateway to ensure cleanup
            await tradingApi.controlIBGateway("stop");
            await fetchControlState();
        } catch (err) {
            console.warn("Failed to stop gateway during cancellation:", err);
        }
    };

    const handleConnectClick = async () => {
        if (!controlState) return;
        if (!isConfigured && !isGatewayActive) {
            handleConfigureClick();
            return;
        }

        // If we are currently connecting, clicking again acts as a cancel action
        if (loading && activeAction === "start") {
            await handleCancel();
            return;
        }

        setLoading(true);
        setGatewayError(null);
        const action = isGatewayActive ? "stop" : "start";
        setActiveAction(action);

        // Cancel any existing controller and establish a new one
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
        }
        const controller = new AbortController();
        abortControllerRef.current = controller;

        try {
            const response = await tradingApi.controlIBGateway(action);
            if (response.success) {
                if (action === "start") {
                    // Poll for connection progress up to 30 seconds
                    let attempts = 0;
                    let maxAttempts = 30;
                    while (attempts < maxAttempts && abortControllerRef.current === controller) {
                        await fetchControlState();

                        try {
                            const status = await tradingApi.getSystemControlState();
                            if (status.success && status.data) {
                                const statusData = status.data;

                                // Update hint/error states if any
                                if (statusData.gateway_hint) {
                                    maxAttempts = Math.max(maxAttempts, 120); // extend window if user action is pending
                                }

                                if (statusData.connection_error) {
                                    const lower = statusData.connection_error.toLowerCase();
                                    const isFatal = lower.includes("invalid credential") ||
                                                    lower.includes("trading mode mismatch") ||
                                                    lower.includes("returned exit status") ||
                                                    lower.includes("failed to decrypt") ||
                                                    lower.includes("fatal");
                                    if (isFatal) {
                                        setGatewayError(statusData.connection_error);
                                        await tradingApi.controlIBGateway("stop");
                                        break;
                                    }
                                }

                                if (statusData.ib_connected) {
                                    break;
                                }
                            }
                        } catch (e) {
                            console.warn("Failed to poll status:", e);
                        }

                        await new Promise(resolve => setTimeout(resolve, 1000));
                        attempts++;
                    }
                }
                await fetchControlState();
            } else {
                setGatewayError(response.error || response.message || `Failed to ${action} gateway`);
            }
        } catch (err: any) {
            if (err.name === 'AbortError') {
                console.log('Broker connection connection cancelled');
            } else {
                setGatewayError(err.message || "Failed to control gateway");
            }
        } finally {
            if (abortControllerRef.current === controller) {
                setLoading(false);
                setActiveAction(null);
                abortControllerRef.current = null;
            }
        }
    };

    const show2FABanner = controlState?.gateway_running && !controlState?.ib_connected;

    return (
        <div className="flex flex-col gap-4 w-full">
            {gatewayError && (
                <div className="rounded-xl bg-error-500/10 border border-error-500/20 px-4 py-3 flex items-start justify-between gap-4 animate-in slide-in-from-top-2 duration-200">
                    <div className="flex items-start gap-2.5 text-sm text-error-500">
                        <AlertTriangle size={16} className="text-error-500 mt-0.5 flex-shrink-0" />
                        <span>{gatewayError}</span>
                    </div>
                    <ThemeButton variant="danger" size="sm"
                        type="button"
                        onClick={() => setGatewayError(null)}

                    >
                        Dismiss
                    </ThemeButton>
                </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-stretch">
                <button
                    type="button"
                    onClick={handleConfigureClick}
                    disabled={loading}
                    className={`w-full min-h-[112px] text-left rounded-xl bg-white/5 border p-4 flex items-center justify-between hover:bg-white/10 transition-colors duration-300 group cursor-pointer focus:outline-none focus:ring-1 focus:ring-neon/40 disabled:opacity-50 ${
                        isConfigured ? 'border-success-500/30 shadow-[0_0_15px_rgba(16,185,129,0.05)]' : 'border-white/10'
                    }`}
                >
                    <div className="flex items-center gap-4">
                        <div className={`h-10 w-10 rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform flex-shrink-0 ${
                            isConfigured ? 'bg-success-500/20 text-success-500' : 'bg-white/10 text-muted'
                        }`}>
                            {loading || showSetupPreparing ? (
                                <Loader2 size={20} className="animate-spin" />
                            ) : (
                                <Settings size={20} />
                            )}
                        </div>
                        <div>
                            <h3 className="text-sm font-semibold text-white">1. Configure IB Connection</h3>
                            <p className="text-xs text-muted">
                                {showSetupPreparing
                                    ? 'Saving setup and preparing broker controls...'
                                    : 'Set up your Interactive Brokers connection details.'}
                            </p>
                        </div>
                    </div>
                    <div className="flex-shrink-0 ml-2">
                        {isConfigured ? (
                            <CheckCircle2 className="w-5 h-5 text-success-500 drop-shadow-[0_0_8px_rgba(16,185,129,0.5)] animate-in zoom-in-50 duration-300" />
                        ) : showSetupPreparing ? (
                            <Loader2 className="w-5 h-5 text-info-500 animate-spin" />
                        ) : (
                            <Circle className="w-5 h-5 text-muted/40" />
                        )}
                    </div>
                </button>

                <div className="flex flex-col gap-3 w-full h-full">
                    <button
                        type="button"
                        onClick={handleConnectClick}
                        disabled={!controlState || showSetupPreparing || (loading && activeAction === "stop")}
                        className={`w-full flex-1 min-h-[112px] text-left rounded-xl bg-white/5 border p-4 flex items-center justify-between hover:bg-white/10 transition-colors duration-300 group cursor-pointer focus:outline-none focus:ring-1 focus:ring-neon/40 disabled:opacity-50 ${
                            (loading && activeAction === "start")
                                ? 'border-warning-500/30 shadow-[0_0_15px_rgba(245,158,11,0.05)] bg-warning-500/5'
                                : isGatewayActive
                                ? 'border-success-500/30 shadow-[0_0_15px_rgba(16,185,129,0.05)]'
                                : 'border-white/10'
                        }`}
                    >
                        <div className="flex items-center gap-4">
                            <div className={`h-10 w-10 rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform flex-shrink-0 ${
                                (loading && activeAction === "start")
                                    ? 'bg-warning-500/20 text-warning-500'
                                    : isGatewayActive
                                    ? 'bg-error-500/20 text-error-500'
                                    : 'bg-info-500/20 text-info-500'
                            }`}>
                                {loading ? (
                                    <Loader2 size={20} className="animate-spin" />
                                ) : isGatewayActive ? (
                                    <Square size={20} />
                                ) : (
                                    <Play size={20} />
                                )}
                            </div>
                            <div>
                                <h3 className="text-sm font-semibold text-white flex items-center gap-2">
                                    {showSetupPreparing ? (
                                        "2. Preparing Broker"
                                    ) : (loading && activeAction === "start") ? (
                                        <>
                                            <span>2. Connecting to Broker...</span>
                                            <span className="text-[10px] bg-error-500/20 text-error-500 border border-error-500/30 px-1.5 py-0.5 rounded animate-pulse">Click to Cancel</span>
                                        </>
                                    ) : isGatewayActive ? (
                                        "2. Disconnect Broker"
                                    ) : (
                                        "2. Connect to Broker"
                                    )}
                                </h3>
                                <p className="text-xs text-muted">
                                    {showSetupPreparing ? (
                                        "Broker controls will be ready in a moment."
                                    ) : (loading && activeAction === "start") ? (
                                        "Establishing secure session. Click anywhere on this button to cancel connection."
                                    ) : isGatewayActive ? (
                                        "Disconnect from Interactive Brokers."
                                    ) : (
                                        "Launch the connection to begin the broker session."
                                    )}
                                </p>
                            </div>
                        </div>
                        <div className="flex-shrink-0 ml-2">
                            {(loading && activeAction === "start") ? (
                                <Loader2 className="w-5 h-5 text-warning-500 animate-spin" />
                            ) : isGatewayActive ? (
                                <CheckCircle2 className="w-5 h-5 text-success-500 drop-shadow-[0_0_8px_rgba(16,185,129,0.5)] animate-in zoom-in-50 duration-300" />
                            ) : (
                                <Circle className="w-5 h-5 text-muted/40" />
                            )}
                        </div>
                    </button>

                    {show2FABanner && (
                        <div className="w-full rounded-xl bg-warning-500/10 border border-warning-500/20 p-3.5 flex flex-col gap-2.5 shadow-[0_0_15px_rgba(245,158,11,0.05)] animate-pulse">
                            <div className="flex items-start gap-2.5">
                                <AlertTriangle size={16} className="text-warning-500 mt-0.5 flex-shrink-0" />
                                <div className="flex-1">
                                    <h4 className="text-xs font-semibold text-warning-500">Action Required: Complete 2FA</h4>
                                    <p className="text-[10px] text-warning-500/80 leading-relaxed mt-1">
                                        {controlState?.gateway_hint || "Gateway connection is starting. Please check your mobile phone for an Interactive Brokers (IB Key) 2FA push notification to approve the secure session."}
                                    </p>
                                </div>
                            </div>
                            <div className="flex items-center gap-1.5 text-[10px] text-warning-500 font-semibold bg-warning-500/20 px-2.5 py-1 rounded border border-warning-500/30 w-max">
                                <Loader2 size={10} className="animate-spin" />
                                <span>Awaiting 2FA Confirmation</span>
                            </div>
                        </div>
                    )}
                </div>

                <div className={`min-h-[112px] rounded-xl bg-white/5 border p-4 flex items-center justify-between hover:bg-white/10 transition-colors duration-300 group ${
                    isBrokerConnected ? 'border-success-500/30 shadow-[0_0_15px_rgba(16,185,129,0.05)]' : 'border-white/10'
                }`}>
                    <div className="flex items-center gap-4">
                        <div className={`h-10 w-10 rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform flex-shrink-0 mt-0.5 ${
                            isBrokerConnected ? 'bg-success-500/20 text-success-500' : 'bg-white/10 text-muted'
                        }`}>
                            <Cloud size={20} />
                        </div>
                        <div>
                            <h3 className="text-sm font-semibold text-white">3. Monitor Anytime</h3>
                            <p className="text-xs text-muted">
                                You can close this window; trading runs 24/7 in the cloud.
                            </p>
                            <p className="text-[10px] text-warning-500/85 mt-1">
                                Reconnects require mobile 2FA; auto-restart runs Sunday night.
                            </p>
                        </div>
                    </div>
                    <div className="flex-shrink-0 ml-2">
                        {isBrokerConnected ? (
                            <CheckCircle2 className="w-5 h-5 text-success-500 drop-shadow-[0_0_8px_rgba(16,185,129,0.5)] animate-in zoom-in-50 duration-300" />
                        ) : (
                            <Circle className="w-5 h-5 text-muted/40" />
                        )}
                    </div>
                </div>

                <GatewayConfigModal
                    isOpen={showConfigModal}
                    onClose={() => setShowConfigModal(false)}
                    onSuccess={handleGatewayConfigSuccess}
                    currentUsername={controlState?.ib_username}
                    currentMode={controlState?.ib_mode}
                    savedGateways={controlState?.saved_gateways}
                />
            </div>
        </div>
    );
};

export default InvestorQuickStart;
